import { useSyncExternalStore } from "react";

export const FLAGS = [
  "runSync",
  "orgAnalytics",
  "machines",
  "certification",
  "compare",
  "matrixGrid",
  "rerun",
  "junitExport",
  "jira",
  "slack",
  "ai",
  "installationOverrides",
  "releaseSourcesEdit",
  "auditRetention",
  "profilePage",
  "performance",
  "waivers",
  "schedules",
  "flaky",
  "payloadDiff",
  "moreAgents",
] as const;

export type Flag = (typeof FLAGS)[number];

const OVERRIDE_KEY = "ats.flags.override";
const listeners = new Set<() => void>();
let version = 0;

function isFlag(v: string): v is Flag {
  return (FLAGS as readonly string[]).includes(v);
}

export function parseFlags(raw: string | undefined): Set<Flag> {
  const out = new Set<Flag>();
  for (const part of (raw ?? "").split(",")) {
    const name = part.trim();
    if (isFlag(name)) out.add(name);
  }
  return out;
}

function mockMode(): boolean {
  return import.meta.env.VITE_MOCK === "1";
}

export function readFlagOverrides(): Partial<Record<Flag, boolean>> {
  if (!mockMode()) return {};
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(OVERRIDE_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null) return {};
    const out: Partial<Record<Flag, boolean>> = {};
    for (const [k, v] of Object.entries(parsed)) if (isFlag(k) && typeof v === "boolean") out[k] = v;
    return out;
  } catch {
    return {};
  }
}

export function setFlagOverride(flag: Flag, value: boolean | null) {
  if (!mockMode()) return;
  const next: Partial<Record<Flag, boolean>> = { ...readFlagOverrides(), [flag]: value ?? undefined };
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(next));
  } catch {
    return;
  }
  version += 1;
  for (const l of listeners) l();
}

export function isEnabled(flag: Flag): boolean {
  const override = readFlagOverrides()[flag];
  if (override !== undefined) return override;
  return parseFlags(import.meta.env.VITE_FEATURES).has(flag);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useFlags(): (flag: Flag) => boolean {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
  return isEnabled;
}

export function useFlag(flag: Flag): boolean {
  return useFlags()(flag);
}
